import React, { useState, useMemo } from 'react';
import { Input, Button } from 'antd';
import {
  SearchOutlined,
  CheckCircleFilled,
  PlusOutlined,
  InfoCircleFilled,
  AuditOutlined,
} from '@ant-design/icons';
import './aiCrew.css';

function getOrdinal(n) {
  if (typeof n !== 'number' || isNaN(n)) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * WhyNotSearch - Answers the question "Why isn't [name] here?" client-side.
 * Searches only client-visible eligible candidates. Blocked worker identities and
 * exclusion reasons are intentionally not exposed in this interface.
 */
export default function WhyNotSearch({
  candidates = [],
  selectedIds = new Set(),
  onAddEmployee,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const searchResult = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return null;
    }

    // 1. Search candidates (both recommended and eligible)
    const exactId = candidates.find(c => String(c.employeeId).toLowerCase() === term);
    const matches = exactId ? [exactId] : candidates.filter(c => String(c.name || '').toLowerCase().includes(term));
    if (matches.length > 1) {
      return { type: 'not_found', message: `Several workers match: ${matches.map(c => `${c.name} (${c.employeeId})`).join(', ')}. Enter the intended person's number to choose one.` };
    }
    const matchingCandidate = matches[0];

    if (matchingCandidate) {
      const isSelected = selectedIds.has(matchingCandidate.employeeId);
      if (isSelected) {
        return {
          type: 'selected',
          candidate: matchingCandidate,
          message: `${matchingCandidate.name} is selected in this crew (Rank ${matchingCandidate.rank || 1}).`,
        };
      } else {
        return {
          type: 'eligible_not_selected',
          candidate: matchingCandidate,
          message: `${matchingCandidate.name} — available in this checked shortlist, ranked ${getOrdinal(matchingCandidate.rank || 1)}. Availability is checked again when you confirm.`,
        };
      }
    }

    // Do not distinguish a blocked worker from any other non-visible worker.
    return {
      type: 'not_found',
      message: 'This worker is not in the displayed shortlist. That alone does not tell us whether they are outside your employee group or unavailable. Check their schedule or use the manual crew screen.',
    };
  }, [searchTerm, candidates, selectedIds]);

  const scrollToRow = (employeeId) => {
    const el = document.querySelector(`[data-testid="candidate-row-${employeeId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ai-crew-row-highlight');
      setTimeout(() => {
        el.classList.remove('ai-crew-row-highlight');
      }, 1800);
    }
  };

  return (
    <div className="ai-crew-why-not-container" data-testid="ai-crew-why-not-search">
      <div className="ai-crew-why-not-header">
        <AuditOutlined style={{ color: '#1677ff' }} />
        <span>Find a Worker in This Shortlist</span>
      </div>

      <div className="ai-crew-why-not-form">
        <span style={{ fontSize: 13, color: '#64748b' }}>
          Search the displayed workers by name or person number:
        </span>
        <Input
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          aria-label="Find a worker by name or person number"
          placeholder="Worker name or person number..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          allowClear
          style={{ width: 280 }}
          size="middle"
        />
      </div>

      {searchResult && (
        <div className="ai-crew-why-not-result">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {searchResult.type === 'selected' && (
              <CheckCircleFilled style={{ color: '#10b981', fontSize: 16 }} />
            )}
            {searchResult.type === 'eligible_not_selected' && (
              <InfoCircleFilled style={{ color: '#1677ff', fontSize: 16 }} />
            )}
            {searchResult.type === 'not_found' && (
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#94a3b8' }} />
            )}
            <span style={{ fontWeight: 500 }}>{searchResult.message}</span>
          </div>

          <div>
            {searchResult.type === 'selected' && (
              <Button
                size="small"
                type="link"
                onClick={() => scrollToRow(searchResult.candidate.employeeId)}
              >
                Highlight row
              </Button>
            )}

            {searchResult.type === 'eligible_not_selected' && (
              <Button
                size="small"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  if (onAddEmployee) {
                    onAddEmployee(searchResult.candidate.employeeId);
                  }
                }}
              >
                Add to Crew
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
